import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

const parameter = 'vanity_discount';
const direction = () => new URL(location.href).searchParams.get(parameter) || '';

class VanityDiscountSort extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.sync();
  }

  sync() {
    const select = this.querySelector('select');
    if (!select) return;
    select.value = direction();
    select.disabled = false;
  }

  changeOrder(event) {
    const select = this.querySelector('select');
    if (!select) return;
    const url = new URL(location.href);
    url.searchParams.delete('page');
    if (/^(asc|desc)$/.test(select.value)) url.searchParams.set(parameter, select.value);
    else url.searchParams.delete(parameter);
    location.assign(url.toString());
  }
}

if (!customElements.get('vanity-discount-sort')) customElements.define('vanity-discount-sort', VanityDiscountSort);

export async function sortCollectionByDiscount(list) {
  const order = direction();
  if (!list.hasAttribute('data-discount-sort-enabled') || !/^(asc|desc)$/.test(order)) return;
  const grid = list.querySelector('[ref="grid"]');
  const status = list.querySelector('[data-discount-status]');
  if (!grid || !status) return;
  const controller = new AbortController();
  window.addEventListener('pagehide', () => controller.abort(), { once: true });
  grid.setAttribute('aria-busy', 'true');
  grid.style.visibility = 'hidden';
  status.hidden = false;
  status.textContent = status.dataset.loading;
  const pagination = list.querySelector('.pagination');
  if (pagination) pagination.hidden = true;
  try {
    const cardsForPage = new Map();
    const currentCards = Array.from(grid.querySelectorAll(':scope > [data-product-id]'));
    const currentPage = Number(currentCards[0]?.dataset.page || 1);
    cardsForPage.set(currentPage, currentCards);
    const lastPage = Number(grid.dataset.lastPage || 1);
    let nextPage = 1;
    const fetchNext = async () => {
      while (nextPage <= lastPage) {
        const page = nextPage++;
        if (page === currentPage) continue;
        const url = new URL(location.href);
        url.searchParams.set('page', String(page));
        url.hash = '';
        const html = await sectionRenderer.getSectionHTML(list.getAttribute('section-id'), false, url);
        if (controller.signal.aborted || !list.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const cards = Array.from(doc.querySelectorAll('[ref="grid"] > [data-product-id]'));
        if (!cards.length) throw new Error('Missing collection page');
        cardsForPage.set(page, cards);
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, lastPage) }, fetchNext));
    if (controller.signal.aborted || !list.isConnected) return;
    const unique = new Map();
    for (let page = 1; page <= lastPage; page++) {
      for (const card of cardsForPage.get(page) || []) unique.set(card.dataset.productId, card);
    }
    const cards = Array.from(unique.values());
    const expected = Number(status.dataset.count);
    if (Number.isFinite(expected) && cards.length !== expected) throw new Error('Incomplete collection');
    cards.sort((a, b) => {
      const difference = Number(a.dataset.discountPercentage || 0) - Number(b.dataset.discountPercentage || 0);
      return order === 'asc' ? difference : -difference;
    });
    for (const card of cards) card.dataset.page = '1';
    grid.dataset.lastPage = '1';
    grid.replaceChildren(...cards);
    grid.dataset.discountSorted = order;
    status.textContent = status.dataset.ready;
    status.classList.add('visually-hidden');
    const url = new URL(location.href);
    url.searchParams.delete('page');
    history.replaceState(history.state, '', url.toString());
  } catch (error) {
    if (controller.signal.aborted) return;
    status.textContent = status.dataset.error;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button-secondary';
    retry.textContent = status.dataset.retry;
    retry.addEventListener('click', () => location.reload(), { once: true });
    status.append(' ', retry);
    if (pagination) pagination.hidden = false;
  } finally {
    grid.removeAttribute('aria-busy');
    grid.style.removeProperty('visibility');
  }
}
